const React = require('react');
const { createElement: h, useState } = React;
const { request, setAccessToken, saveCredentials, getSavedCredentials } = require('../lib/apiClient');

function KeyIcon() {
  return h('svg', { className: 'cap-provider-icon', width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4' })
  );
}

function BellIcon() {
  return h('svg', { className: 'cap-provider-icon', width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9' }),
    h('path', { d: 'M13.73 21a2 2 0 01-3.46 0' })
  );
}

function LanguageIcon() {
  return h('svg', { className: 'cap-provider-icon', width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('circle', { cx: 12, cy: 12, r: 9 }),
    h('path', { d: 'M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18' })
  );
}

// Reusable on/off switch for settings rows.
function ToggleSwitch(props) {
  return h('button', {
    type: 'button',
    role: 'switch',
    'aria-checked': props.on ? 'true' : 'false',
    className: 'acc-toggle' + (props.on ? ' acc-toggle--on' : ''),
    disabled: !!props.disabled,
    onClick: props.onClick
  }, h('span', { className: 'acc-toggle__knob' }));
}

// Opt-in email notifications when a run FAILS (credit/node-exec/rate limit, or a
// runtime error). Saved server-side; the engine sends the mail (throttled so a
// high-volume user can't be flooded).
function EmailNotificationsCard(props) {
  var user = props.user || {};
  var [email, setEmail] = useState(user.email || '');
  var [enabled, setEnabled] = useState(!!user.emailNotifications);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState('');
  var [success, setSuccess] = useState('');

  function handleSave() {
    setError('');
    setSuccess('');
    if (enabled && !email.trim()) {
      setError('Add an email address to enable email notifications.');
      return;
    }
    setSaving(true);
    request('/user/notifications', {
      method: 'PUT',
      body: { email: email.trim(), emailNotifications: enabled }
    })
      .then(function(result) {
        if (result) {
          setEmail(result.email || '');
          setEnabled(!!result.emailNotifications);
        }
        setSuccess('Saved.');
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not save notification settings.');
      })
      .finally(function() {
        setSaving(false);
      });
  }

  return h('article', { className: 'cap-card acc-card' },
    h('div', { className: 'cap-card__main' },
      h('div', { className: 'cap-card__icon' }, h(BellIcon)),
      h('div', { className: 'cap-card__text acc-card__text' },
        h('div', { className: 'cap-card__header' },
          h('h2', { className: 'cap-card__title' }, 'Email notifications')
        ),
        h('p', { className: 'cap-card__description' }, 'Get an email when a workflow run fails — credit or node-execution limit reached, per-minute rate hit, or a runtime error. Throttled so you never get flooded.'),
        h('div', { className: 'acc-form' },
          h('label', { className: 'acc-field' },
            h('span', { className: 'acc-field__label' }, 'Notification email'),
            h('input', {
              type: 'email',
              className: 'acc-field__input',
              value: email,
              onChange: function(e) { setEmail(e.target.value); },
              autoComplete: 'email',
              placeholder: 'you@example.com',
              disabled: saving
            })
          ),
          h('div', { className: 'acc-toggle-row' },
            h('span', { className: 'acc-toggle-row__label' }, 'Email me when a run fails'),
            h(ToggleSwitch, { on: enabled, disabled: saving, onClick: function() { setEnabled(!enabled); } })
          ),
          error ? h('p', { className: 'acc-form__error' }, error) : null,
          success ? h('p', { className: 'acc-form__success' }, success) : null,
          h('div', { className: 'acc-form__actions' },
            h('button', { type: 'button', className: 'cap-connect-btn', disabled: saving, onClick: handleSave },
              saving ? 'Saving...' : 'Save')
          )
        )
      )
    )
  );
}

function PasswordField(props) {
  return h('label', { className: 'acc-field' },
    h('span', { className: 'acc-field__label' }, props.label),
    h('input', {
      type: 'password',
      className: 'acc-field__input',
      value: props.value,
      onChange: function(e) { props.onChange(e.target.value); },
      autoComplete: props.autoComplete || 'new-password',
      disabled: !!props.disabled,
      placeholder: props.placeholder || ''
    })
  );
}

function ChangePasswordCard(props) {
  var user = props.user;
  var [currentPassword, setCurrentPassword] = useState('');
  var [newPassword, setNewPassword] = useState('');
  var [confirmPassword, setConfirmPassword] = useState('');
  var [submitting, setSubmitting] = useState(false);
  var [error, setError] = useState('');
  var [success, setSuccess] = useState('');

  function reset() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }

  function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password.');
      return;
    }

    setSubmitting(true);
    request('/user/password', {
      method: 'PUT',
      body: { currentPassword: currentPassword, newPassword: newPassword }
    })
      .then(function(result) {
        // Server rotates the session — swap in the fresh token so this tab
        // stays signed in. Other devices/tabs are signed out by design.
        if (result && result.token) {
          setAccessToken(result.token);
        }
        // Refresh saved credentials if the user previously checked "remember me",
        // otherwise they'd auto-fill the old password on next login.
        var saved = getSavedCredentials();
        if (saved && user && saved.username === user.username) {
          saveCredentials(saved.username, newPassword);
        }
        reset();
        setSuccess('Password updated.');
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not update password.');
      })
      .finally(function() {
        setSubmitting(false);
      });
  }

  return h('article', { className: 'cap-card acc-card' },
    h('div', { className: 'cap-card__main' },
      h('div', { className: 'cap-card__icon' }, h(KeyIcon)),
      h('div', { className: 'cap-card__text acc-card__text' },
        h('div', { className: 'cap-card__header' },
          h('h2', { className: 'cap-card__title' }, 'Change password')
        ),
        h('p', { className: 'cap-card__description' }, 'Use at least 6 characters. After updating, other devices will be signed out.'),
        h('form', { className: 'acc-form', onSubmit: handleSubmit },
          h(PasswordField, {
            label: 'Current password',
            value: currentPassword,
            onChange: setCurrentPassword,
            autoComplete: 'current-password',
            disabled: submitting
          }),
          h(PasswordField, {
            label: 'New password',
            value: newPassword,
            onChange: setNewPassword,
            disabled: submitting
          }),
          h(PasswordField, {
            label: 'Confirm new password',
            value: confirmPassword,
            onChange: setConfirmPassword,
            disabled: submitting
          }),
          error ? h('p', { className: 'acc-form__error' }, error) : null,
          success ? h('p', { className: 'acc-form__success' }, success) : null,
          h('div', { className: 'acc-form__actions' },
            h('button', {
              type: 'submit',
              className: 'cap-connect-btn',
              disabled: submitting
            }, submitting ? 'Updating...' : 'Update password')
          )
        )
      )
    )
  );
}

function UsernameCard(props) {
  var user = props.user || {};
  var username = user.username || '-';
  var plan = user.plan || 'free';

  return h('article', { className: 'cap-card' },
    h('div', { className: 'cap-card__main' },
      h('div', { className: 'cap-card__icon' },
        h('svg', { className: 'cap-provider-icon', width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('path', { d: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2' }),
          h('circle', { cx: 12, cy: 7, r: 4 })
        )
      ),
      h('div', { className: 'cap-card__text' },
        h('div', { className: 'cap-card__header' },
          h('h2', { className: 'cap-card__title' }, username),
          h('span', { className: 'cap-status cap-status--connected' }, plan.toUpperCase())
        ),
        h('p', { className: 'cap-card__description' }, 'Your sign-in username. To change it, contact support.')
      )
    )
  );
}

function LanguageCard(props) {
  var language = props.language === 'da' ? 'da' : 'en';
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState('');

  function handleChange(event) {
    var nextLanguage = event.target.value === 'da' ? 'da' : 'en';
    if (nextLanguage === language || !props.onChangeLanguage) return;
    setError('');
    setSaving(true);
    Promise.resolve(props.onChangeLanguage(nextLanguage))
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not save language.');
      })
      .finally(function() {
        setSaving(false);
      });
  }

  return h('article', { className: 'cap-card acc-card' },
    h('div', { className: 'cap-card__main' },
      h('div', { className: 'cap-card__icon' }, h(LanguageIcon)),
      h('div', { className: 'cap-card__text acc-card__text' },
        h('div', { className: 'cap-card__header' },
          h('h2', { className: 'cap-card__title' }, 'Language')
        ),
        h('p', { className: 'cap-card__description' }, 'Choose the language used throughout AlleFlow. This preference follows your account across devices.'),
        h('label', { className: 'acc-field' },
          h('span', { className: 'acc-field__label' }, 'Interface language'),
          h('select', {
            className: 'acc-field__input',
            value: language,
            disabled: saving,
            onChange: handleChange
          },
            h('option', { value: 'en' }, 'English'),
            h('option', { value: 'da' }, 'Danish')
          )
        ),
        saving ? h('p', { className: 'acc-form__success' }, 'Saving...') : null,
        error ? h('p', { className: 'acc-form__error' }, error) : null
      )
    )
  );
}

function AccountSettingsPage(props) {
  var user = props && props.user ? props.user : null;
  return h('main', { className: 'cap-root' },
    h('header', { className: 'cap-header' },
      h('p', { className: 'cap-eyebrow' }, 'User account'),
      h('h1', { className: 'cap-title' }, 'Account settings'),
      h('p', { className: 'cap-copy' }, 'Manage your sign-in details. Connected services live under "Accounts".')
    ),
    h('section', { className: 'cap-list' },
      h(UsernameCard, { user: user }),
      h(LanguageCard, {
        language: props && props.language,
        onChangeLanguage: props && props.onChangeLanguage
      }),
      h(EmailNotificationsCard, { user: user }),
      h(ChangePasswordCard, { user: user })
    )
  );
}

module.exports = AccountSettingsPage;
